import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v7";

/**
 * Manages spine animation for Logo components
 * Handles initialization, scaling, and animation playback
 */
class LogoController {
  constructor(container, scaledWidth, scaledHeight, deviceConfig) {
    this.container = container;
    this.scaledWidth = scaledWidth;
    this.scaledHeight = scaledHeight;
    this.deviceConfig = deviceConfig;
    this.spine = null;
    this.isInitialized = false;
    this.desiredVisibility = false; // Store desired visibility state

    // Defer spine creation to avoid blocking the main thread
    requestAnimationFrame(() => {
      this.initializeSpineAnimation();
    });
  }

  initializeSpineAnimation() {
    this.spine = this.createSpine();
    if (!this.spine) {
      return;
    }

    if (
      !this.spine.skeleton ||
      !this.spine.state ||
      !this.spine.skeleton?.data
    ) {
      return;
    }

    this.spine.position.set(0, 0);
    this.spine.zIndex = 5;
    this.spine.visible = true;
    this.spine.alpha = 0.001;
    this.spine.skeleton.setToSetupPose();
    this.container.addChild(this.spine);

    if (this.spine.parent !== this.container) {
      return;
    }

    this.applySpineScaling();
    this.playAnimation("idle", true);
    this.isInitialized = true;
    this.spine.alpha = this.desiredVisibility ? 1 : 0.001;
  }

  createSpine() {
    try {
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "logo-skeleton";
      const atlasAlias = "logo-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
        return null;
      }

      const spine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      if (!spine || !spine.skeleton || !spine.state) {
        return null;
      }

      if (!spine.skeleton.data || !spine.skeleton.data.animations) {
        return null;
      }

      return spine;
    } catch (error) {
      return null;
    }
  }

  playAnimation(animationName, loop = false) {
    if (!this.spine || !this.spine.state) {
      return;
    }

    const animations = this.spine.skeleton.data.animations;
    const animation = animations.find((anim) => anim.name === animationName);

    if (!animation) {
      return;
    }

    this.spine.state.setAnimation(0, animationName, loop);
  }

  getNativeDimensions() {
    if (!this.spine?.skeleton?.data) {
      return { width: 0, height: 0, x: 0, y: 0 };
    }

    const skeletonData = this.spine.skeleton.data;
    return {
      width: skeletonData.width || 0,
      height: skeletonData.height || 0,
      x: skeletonData.x || 0,
      y: skeletonData.y || 0,
    };
  }

  applySpineScaling() {
    if (!this.spine || !this.scaledWidth || !this.scaledHeight) {
      return;
    }

    if (!this.spine.skeleton || !this.spine.state) {
      return;
    }

    const nativeDims = this.getNativeDimensions();

    if (!nativeDims.width || !nativeDims.height) {
      const spineBounds = this.spine.getLocalBounds();
      if (!spineBounds || spineBounds.width <= 0 || spineBounds.height <= 0) {
        setTimeout(() => {
          this.applySpineScaling();
        }, 100);
        return;
      }

      const scaleX = this.scaledWidth / spineBounds.width;
      const scaleY = this.scaledHeight / spineBounds.height;
      const scale = Math.min(scaleX, scaleY);

      this.spine.scale.set(scale, scale);
      this.spine.position.set(0, 0);
      return;
    }

    const scaleX = this.scaledWidth / nativeDims.width;
    const scaleY = this.scaledHeight / nativeDims.height;
    const scale = Math.min(scaleX, scaleY);

    this.spine.scale.set(scale, scale);
    this.spine.position.set(0, 0);
  }

  updateDimensions(newScaledWidth, newScaledHeight, newDeviceConfig) {
    this.scaledWidth = newScaledWidth;
    this.scaledHeight = newScaledHeight;
    if (newDeviceConfig) {
      this.deviceConfig = newDeviceConfig;
    }
    if (this.spine && this.isInitialized) {
      this.applySpineScaling();
    }
  }

  setVisible(visible) {
    this.desiredVisibility = visible;
    if (this.spine && this.isInitialized) {
      this.spine.alpha = visible ? 1 : 0.001;
    }
  }

  getAvailableAnimations() {
    if (!this.spine?.skeleton?.data?.animations) {
      return [];
    }
    return this.spine.skeleton.data.animations.map((anim) => anim.name);
  }

  cleanup() {
    if (this.spine) {
      try {
        if (this.spine.state) {
          this.spine.state.clearListeners();
          this.spine.state.clearTracks();
        }
        if (this.container?.children.includes(this.spine)) {
          this.container.removeChild(this.spine);
        }
        this.spine.destroy();
        this.spine = null;
        this.isInitialized = false;
      } catch (error) {
        this.spine = null;
        this.isInitialized = false;
      }
    }
  }
}

export default LogoController;
